import { TopicsPanelProvider, useScopedTopicsInstance, useTopicsDataStore, useTopicsProvider, useTopicsStoreProvider } from "./Context";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import Thread from "./components/Thread";
import { AnimatePresence } from "framer-motion";

import FloatingPanel, { createThreadMarker, destroyThreadMarker } from "./components/FloatingPanel";

export default function FloatingTopics() {
	const topicsInstance = useScopedTopicsInstance();
	const { onTravel, onCheck, API_CLIENT, namespace } = topicsInstance;
	const [activeThreadId, setActiveThreadId] = useState(null);
	const [activeThreadIds, setActiveThreadIds] = useState([]);
	const [pendingThreadId, setPendingThreadId] = useState(null); // <-- waits until animation ends

	const activeTab = 'all';

	const threadIds = useTopicsStoreProvider(s => s.threadIds);
	const threads = useTopicsDataStore(s => s.threads);

	const { data = [], isSuccess } = useQuery({
		queryKey: ["threads", "floating-threads", namespace],
		queryFn: async () => (await API_CLIENT.get("")).data,
		enabled: !!namespace,
	});

	const transformElement = (selector, id) => {
		createThreadMarker({
			attachedTo: document.querySelector(selector),
			onClick: e => {
				e.stopPropagation();
				setActiveThreadId(id);
			},
			isResolved: threads.get(id)?.isResolved,
		})
	};

	const handleTravelAfterClose = async nextId => {
		setPendingThreadId(nextId);
		setActiveThreadId(null); // triggers exit animation first
	};

	const handleNext = () => {
		const nextThread =
			activeThreadIds[(activeThreadIds.indexOf(activeThreadId) + 1) % activeThreadIds.length];
		handleTravelAfterClose(nextThread);
	};

	const handlePrev = () => {
		const prevThread =
			activeThreadIds[
			(activeThreadIds.indexOf(activeThreadId) - 1 + activeThreadIds.length) %
			activeThreadIds.length
			];
		handleTravelAfterClose(prevThread);
	};

	const handleClose = () => setActiveThreadId(null);

	// after animation ends and no active thread, perform travel
	useEffect(() => {
		if (!activeThreadId && pendingThreadId) {
			(async () => {
				await onTravel(pendingThreadId);
				setActiveThreadId(pendingThreadId);
				setPendingThreadId(null);
			})();
		}
	}, [activeThreadId, pendingThreadId]);

	useEffect(() => {
		if (activeThreadId && !activeThreadIds.includes(activeThreadId)) setActiveThreadId(null);
	}, [activeThreadIds]);

	useEffect(() => {
		if (isSuccess) topicsInstance.setThreadsAndComments(data);
	}, [data, isSuccess]);

	useEffect(() => {
		const updateActiveThreads = () => {
			const activeThreads = new Set();

			threadIds.forEach((threadId, id) => {
				const t = threads.get(threadId);
				const selector = t?.selector;

				// check element against active tabs
				const isActive = activeTab === 'all' || (activeTab === 'unresolved' && !t.isResolved) ||
					(activeTab === 'resolved' && t.isResolved) ||
					(activeTab === 'pinned' && t.isPinned);

				if (isActive && selector && onCheck(id)) {
					transformElement(selector, id);
					activeThreads.add(id);
				}
			});

			setActiveThreadIds(prev => {
				if (
					prev.length === activeThreads.size &&
					prev.every(id => {
						return activeThreads.has(id)
					})
				)
					return prev;

				return Array.from(activeThreads);
			});
		};


		const observer = new MutationObserver(mutationsList => {
			for (const mutation of mutationsList) {
				if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
					updateActiveThreads();
				}
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });
		updateActiveThreads();
		return () => observer.disconnect();
	}, [threads, threadIds, onCheck, activeTab]);

	useEffect(() => {
		activeThreadIds.forEach(id => {
			const t = threads.get(id);
			t.selector && transformElement(t.selector, id);
		});

		return () => {
			activeThreadIds.forEach(id => {
				const t = threads.get(id);
				t.selector && destroyThreadMarker(document.querySelector(t.selector));
			});
		};
	}, [activeThreadIds]);

	return (
		<TopicsPanelProvider value={{ activeTab }}>
			<AnimatePresence mode="wait">
				{activeThreadId && (
					<ThreadPanel
						key={activeThreadId}
						id={activeThreadId}
						onNext={handleNext}
						onPrev={handlePrev}
						onClose={handleClose}
					/>
				)}
			</AnimatePresence>
		</TopicsPanelProvider>
	);
}

const ThreadPanel = ({ id, onNext, onPrev, onClose }) => {
	const thread = useTopicsDataStore(s => s.threads.get(id));
	if (!thread) return null;

	const { selector } = thread;
	const el = document.querySelector(selector);
	if (!el) return null;

	return <FloatingPanel mode="wait" panelKey={`${id}`} attachedTo={el} width={500} height={300}>
		<div className="flex gap-2 h-7 px-2">
			<button className="btn bg-transparent text-primary btn-icon" onClick={onPrev}>
				arrow_back_ios_new
			</button>
			<button className="btn bg-transparent text-primary btn-icon" onClick={onNext}>
				arrow_forward_ios
			</button>
			<button
				className="btn bg-transparent text-primary btn-icon ml-auto text-2xl"
				onClick={onClose}
			>
				close
			</button>
		</div>
		<Thread id={id} inFloatingPanel={true} />
	</FloatingPanel>

};
